
import { UsageRecord } from '../../types';
import { getCurrentUserId } from '../../supabase';
import { getUsageRecords, setUsageRecords } from './core';
import { logError } from '@/utils/errorLogger';
import { 
  fetchRemoteUsageRecords, 
  addUsageRecordToSupabase, 
  updateProductStockInSupabase 
} from './remote';
import { 
  addUsageRecordToLocalStorage 
} from './local';

/**
 * Fetch usage records from Supabase or fall back to localStorage
 */
export const fetchUsageRecords = async (): Promise<UsageRecord[]> => {
  try {
    const remoteRecords = await fetchRemoteUsageRecords();
    
    // If remote fetch succeeded, update localStorage cache and return
    if (remoteRecords) {
      setUsageRecords(remoteRecords);
      return remoteRecords;
    }
    
    // Fall back to localStorage if remote fetch failed
    return getUsageRecords();
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in fetchUsageRecords: ${error.message}`,
      source: 'fetchUsageRecords',
      stack: error.stack,
    });
    console.error('Error in fetchUsageRecords:', error);
    return getUsageRecords(); // Fallback to localStorage
  }
};

/**
 * Add a usage record to both local storage and Supabase
 */
export const addUsageRecord = async (record: UsageRecord): Promise<boolean> => {
  try {
    // Always add to localStorage first for offline support
    addUsageRecordToLocalStorage(record);
    
    // Check if user is authenticated
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available, saved to localStorage only');
      return false;
    }

    // Add record to Supabase
    const recordAdded = await addUsageRecordToSupabase(record, userId);
    if (!recordAdded) {
      return false;
    }

    // Update product stock in Supabase
    await updateProductStockInSupabase(record.productId, record.quantity, userId);

    return true;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in addUsageRecord: ${error.message}`,
      source: 'addUsageRecord',
      stack: error.stack,
    });
    console.error('Error in addUsageRecord:', error);
    return false;
  }
};
